import React, { useState, useEffect, useRef, useMemo } from "react";
import { downloadCsv } from "../common/sharedFunctions";
import MaterialTable from "material-table";
import { useSelector, useDispatch } from "react-redux";
import CircularLoading from "../components/CircularLoading";
import { api } from "common";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams,useLocation } from "react-router-dom";
import AlertDialog from "../components/AlertDialog";
import moment from 'moment/min/moment-with-locales';
import EditIcon from '@mui/icons-material/Edit';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import { Box, Typography, Badge, Tooltip } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import BlankTable from '../components/Table/BlankTable';
import localization from '../components/Table/Localization';
import {
  C2,
  C2_FONT,
  c2TableTheme,
  c2TableOptions,
  c2TableStyle,
  c2Toolbar,
  c2Localization,
  C2TableCard,
  C2AddButton,
  C2EmptyState,
  C2UserAvatar,
  C2DocsChip,
  C2Switch,
  C2Select,
  C2VerifiedBadge,
  hasPendingDocReview,
  isUserDocVerified,
  getUserDocTimestamp,
  isReuploadedAfterReject,
  C2DocsBadge,
} from '../components/Table/C2UserTable';
import VerifyIdDocumentModal from '../components/VerifyIdDocumentModal';

export default function Users() {
  const navigate = useNavigate();
  const {id} = useParams();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const {
    editUser,
    deleteUser,
    fetchUsersOnce,
    fetchZonificacionCities,
  } = api;
  const settings = useSelector((state) => state.settingsdata.settings);
  const [data, setData] = useState([]);
  const [sortedData, SetSortedData] = useState([]);
  const [cityFilter, setCityFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortFilter, setSortFilter] = useState("createdAt_desc");
  const [docsUser, setDocsUser] = useState(null);
  const staticusers = useSelector((state) => state.usersdata.staticusers);
  const zonificacionCities = useSelector((state) => state.zonificacion?.cities);
  const auth = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const loaded = useRef(false);
  const {state} = useLocation()
  const [currentPage,setCurrentPage] = useState(null)
  
  useEffect(()=>{
    setCurrentPage(state?.pageNo)
  },[state])
  
  const HandalePageChange = (page)=>{
    setCurrentPage(page)
  }
  
  const citiesById = useMemo(() => {
    const map = {};
    (zonificacionCities || []).forEach((c) => {
      if (c?.id) map[c.id] = c.name || c.id;
    });
    return map;
  }, [zonificacionCities]);

  const cityFilterOptions = useMemo(() => {
    const options = new Map();
    (zonificacionCities || []).forEach((c) => {
      if (c?.id) options.set(c.id, c.name || c.id);
    });
    (data || []).forEach((d) => {
      if (d.cityId && !options.has(d.cityId)) {
        options.set(d.cityId, d.cityName || d.cityId);
      }
    });
    return Array.from(options.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, "es"));
  }, [zonificacionCities, data]);

  useEffect(() => {
    dispatch(fetchUsersOnce());
    dispatch(fetchZonificacionCities());
  }, [dispatch, fetchUsersOnce, fetchZonificacionCities]);

  useEffect(() => {
    if (staticusers) {
      setData(
        staticusers
          .filter(
            (user) =>
              user.usertype === "customer" &&
              ((user.fleetadmin === auth.profile.uid &&
                auth.profile.usertype === "fleetadmin") ||
                auth.profile.usertype === "admin")
          )
          .map((user) => ({
            ...user,
            cityName:
              user.cityName ||
              (user.cityId ? citiesById[user.cityId] : "") ||
              "",
          }))
      );
    } else {
      setData([]);
    }
    loaded.current = true;
  }, [staticusers, auth.profile, citiesById]);

  useEffect(()=>{
    if(data){
      SetSortedData(data.sort((a,b)=>(moment(b.createdAt) - moment(a.createdAt))))
    }
  },[data])

  const pendingDocsCount = useMemo(() => {
    return (sortedData || []).filter(hasPendingDocReview).length;
  }, [sortedData]);

  const filteredData = useMemo(() => {
    let list = (sortedData || []).filter((d) => {
      // 1. Ciudad
      if (cityFilter) {
        if (cityFilter === "__none__") {
          if (d.cityId || d.cityName) return false;
        } else if (d.cityId !== cityFilter && d.cityName !== cityFilter) {
          return false;
        }
      }

      // 2. Estado de verificación y documentos
      if (statusFilter) {
        if (statusFilter === "pending_docs") {
          if (!hasPendingDocReview(d)) return false;
        } else if (statusFilter === "verified") {
          if (!isUserDocVerified(d)) return false;
        } else if (statusFilter === "rejected") {
          if (d.verifyIdStatus !== "rejected") return false;
        } else if (statusFilter === "approved") {
          if (!d.approved) return false;
        }
      }

      return true;
    });

    // 3. Ordenamiento (Registros más recientes vs Últimos documentos subidos primero)
    if (sortFilter === "docs_desc") {
      return [...list].sort((a, b) => {
        const timeB = getUserDocTimestamp(b);
        const timeA = getUserDocTimestamp(a);
        if (timeB !== timeA) return timeB - timeA;
        return moment(b.createdAt) - moment(a.createdAt);
      });
    }

    return list;
  }, [sortedData, cityFilter, statusFilter, sortFilter]);

  const columns = [
    { title: t('createdAt'), field: 'createdAt', editable:'never', defaultSort:'desc',render: rowData => rowData.createdAt? moment(rowData.createdAt).format('lll'):null},

    {
      title: t("first_name"),
      field: "firstName",
      render: (rowData) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
          <Typography sx={{ fontFamily: C2_FONT, fontSize: "0.85rem", fontWeight: 600, color: C2.dark }}>
            {rowData.firstName || ""}
          </Typography>
          <C2VerifiedBadge user={rowData} />
        </Box>
      ),
    },
    { title: t("last_name"), field: "lastName" },
    {
      title: "Ciudad",
      field: "cityName",
      editable: "never",
      render: (rowData) => rowData.cityName || "—",
    },
    {
      title: t("mobile"),
      field: "mobile",
      editable: "onAdd",
      render: (rowData) =>
        settings.AllowCriticalEditsAdmin ? rowData.mobile : t("hidden_demo"),
    },
    {
      title: t("email"),
      field: "email",
      editable: "onAdd",
      render: (rowData) =>
        settings.AllowCriticalEditsAdmin ? rowData.email : t("hidden_demo"),
    },
    {
      title: t("profile_image"),
      field: "profile_image",
      render: (rowData) => (
        <C2UserAvatar src={rowData.profile_image} firstName={rowData.firstName} lastName={rowData.lastName} />
      ),
      editable: "never",
    },
    {
      title: t("account_approve"),
      field: "approved",
      type: "boolean",
      render: (rowData) => (
        <C2Switch
          disabled={!settings.AllowCriticalEditsAdmin}
          checked={rowData.approved}
          onChange={() => handelApproved(rowData)}
        />
      ),
    },
  ];


  const [selectedRow, setSelectedRow] = useState(null);

  const [commonAlert, setCommonAlert] = useState({ open: false, msg: "" });

  const handleCommonAlertClose = (e) => {
    e.preventDefault();
    setCommonAlert({ open: false, msg: "" });
  };
  const handelApproved = (rowData) => {
    const updatedUser = { ...rowData, approved: !rowData.approved };
    dispatch(editUser(updatedUser.id, updatedUser));
    dispatch(fetchUsersOnce());
    return updatedUser;
  };

  const tableComponents = c2Toolbar(
    pendingDocsCount > 0 ? (
      <Tooltip title="Filtrar usuarios que subieron documento pendiente de verificación">
        <Box
          onClick={() => setStatusFilter(statusFilter === "pending_docs" ? "" : "pending_docs")}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.7,
            px: 1.4,
            py: 0.6,
            borderRadius: "10px",
            bgcolor: statusFilter === "pending_docs" ? "#E65100" : "#FFF3E0",
            color: statusFilter === "pending_docs" ? "#FFFFFF" : "#E65100",
            border: "1.5px solid #FF9800",
            cursor: "pointer",
            fontWeight: 800,
            fontSize: "0.8rem",
            fontFamily: C2_FONT,
            boxShadow: "0 2px 8px rgba(255,152,0,0.2)",
            transition: "all 0.2s ease",
            "&:hover": {
              transform: "translateY(-1px)",
              boxShadow: "0 4px 12px rgba(255,152,0,0.35)",
            },
          }}
        >
          <NotificationsActiveOutlinedIcon sx={{ fontSize: 17 }} />
          <span>{pendingDocsCount} {pendingDocsCount === 1 ? "doc por revisar" : "docs por revisar"}</span>
        </Box>
      </Tooltip>
    ) : null,
    <C2Select
      id="rider-sort-filter"
      label="Ordenar por"
      value={sortFilter}
      onChange={(e) => setSortFilter(e.target.value)}
      minWidth={230}
      options={[
        { value: "createdAt_desc", label: "Registro (recientes primero)" },
        { value: "docs_desc", label: "Últimos docs subidos primero" },
      ]}
    />,
    <C2Select
      id="rider-status-filter"
      label="Estado doc"
      value={statusFilter}
      onChange={(e) => setStatusFilter(e.target.value)}
      minWidth={170}
      options={[
        { value: "", label: "Todos los estados" },
        { value: "pending_docs", label: "Por revisar documentos" },
        { value: "verified", label: "Verificados" },
        { value: "rejected", label: "Rechazados" },
      ]}
    />,
    <C2Select
      id="rider-city-filter"
      label="Filtrar por ciudad"
      value={cityFilter}
      onChange={(e) => setCityFilter(e.target.value)}
      minWidth={180}
      options={[
        { value: "", label: "Todas" },
        { value: "__none__", label: "Sin ciudad" },
        ...cityFilterOptions.map((c) => ({ value: c.id, label: c.name })),
      ]}
    />,
    <C2AddButton
      label="Agregar usuario"
      onClick={() => navigate("/users/edituser/customer",{state:{pageNo:currentPage}})}
    />
  );
  const tableLocalization = c2Localization(
    localization(t),
    <C2EmptyState
      icon={PersonOutlineIcon}
      message={
        cityFilter
          ? "No hay usuarios para este filtro. Prueba con otra ciudad."
          : "Aún no hay usuarios registrados. Cuando alguien se registre en Conecta2 aparecerá aquí."
      }
    />
  );

  return !loaded.current ? (
    <CircularLoading />
  ) : (
    <div>
      <ThemeProvider theme={c2TableTheme}>
      <C2TableCard>
       {sortedData?.length >0 ? 
      <MaterialTable
        title={t("riders_title")}
        columns={columns}
        onChangePage={(page)=>HandalePageChange(page)}
        style={c2TableStyle(isRTL)}
        data={filteredData}
        components={tableComponents}
        onRowClick={(evt, selectedRow) =>
          setSelectedRow(selectedRow.tableData.id)
        }
        options={{
          ...c2TableOptions(selectedRow),
          initialPage:state?.pageNo,
          exportCsv: (columns, data) => {
            let hArray = [];
            const headerRow = columns.map((col) => {
              if (typeof col.title === "object") {
                return col.title.props.text;
              }
              hArray.push(col.field);
              return col.title;
            });
            const dataRows = data.map(({ tableData, ...row }) => {
              row.createdAt =
                new Date(row.createdAt).toLocaleDateString() +
                " " +
                new Date(row.createdAt).toLocaleTimeString();
              let dArr = [];
              for (let i = 0; i < hArray.length; i++) {
                dArr.push(row[hArray[i]]);
              }
              return Object.values(dArr);
            });
            const { exportDelimiter } = ",";
            const delimiter = exportDelimiter ? exportDelimiter : ",";
            const csvContent = [headerRow, ...dataRows]
              .map((e) => e.join(delimiter))
              .join("\n");
            const csvFileName = "download.csv";
            downloadCsv(csvContent, csvFileName);
          },
          exportButton: {
            csv: settings.AllowCriticalEditsAdmin,
            pdf: false,
          },
          maxColumnSort: "all_columns",
        }}
        localization={tableLocalization}
        editable={{
          onRowDelete: (oldData) =>
            settings.AllowCriticalEditsAdmin
              ? new Promise((resolve) => {
                  setTimeout(() => {
                    resolve();
                    dispatch(deleteUser(oldData.id));
                    dispatch(fetchUsersOnce());
                  }, 600);
                })
              : new Promise((resolve) => {
                  setTimeout(() => {
                    resolve();
                    alert(t("demo_mode"));
                  }, 600);
                }),
        }}
        actions={[
          (rowData) => ({
        tooltip: t("edit"),
          icon: () => (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                marginRight:0
              }}
            >
              <EditIcon />  
            </div>
          ),
          onClick: (event, rowData) =>{
            navigate(`/users/edituser/customer/${rowData.id}`,{state:{pageNo:currentPage}})
          }
        }),
          {
            icon: "info",
            tooltip: t("profile_page_subtitle"),
            onClick: (event, rowData) => {
              navigate(`/users/customerdetails/${rowData.id}`,{state:{pageNo:currentPage}});
              
            },
          },
          
          (rowData) => {
            const isPending = hasPendingDocReview(rowData);
            const isReup = isReuploadedAfterReject(rowData);
            const isVerified = isUserDocVerified(rowData);
            return {
              icon: () => (
                <C2DocsBadge isPending={isPending} isReup={isReup}>
                  <C2DocsChip label={t("documents")} />
                </C2DocsBadge>
              ),
              tooltip: isPending
                ? "⚠️ Documento subido pendiente de revisión y aprobación"
                : isVerified
                ? "Documento verificado y aprobado"
                : t("documents"),
              onClick: () => setDocsUser(rowData),
            };
          },
        ]}
      />
      :  <BlankTable title={t('riders_title')}  columns={columns}
      actions={[]}
      components={tableComponents}
      style={c2TableStyle(isRTL)}
      data={[]} localization={tableLocalization} options={c2TableOptions(null)}/>}
      </C2TableCard>
    </ThemeProvider> 
      <VerifyIdDocumentModal
        open={!!docsUser}
        user={docsUser}
        onClose={() => setDocsUser(null)}
      />
      <AlertDialog open={commonAlert.open} onClose={handleCommonAlertClose}>
        {commonAlert.msg}
      </AlertDialog>
    </div>
  );
}
